// @flow
import _ from 'lodash'
import React, { Component } from 'react'
import { translations, translateFunc } from '../../shared/localize'
import ExpandingDropdown from './ExpandingDropdown'
import PlayPauseButton from '../controls/PlayPauseButton'
import AjaxStateSwitcher from '../controls/AjaxStateSwitcher'
import Button from '../controls/SmallRoundedButton'
import styled, { type StyledComponent } from 'styled-components'
import { linkColor } from '../controls/colors'

const Wrapper: StyledComponent<Object, any, any> = styled.div`
  padding: 5px;
  font-size: 14px;
  text-align: left;
  min-width: 300px;
`

const Spacer: StyledComponent<Object, any, any> = styled.div`
  height: 15px;
`

const ProviderStatusNote: StyledComponent<Object, any, any> = styled.div`
  &:hover {
    opacity: 0.8;
  }
  ${({ isActive }: Object) => (isActive ? `color: ${linkColor};` : '')};
`

const ProviderStatusWrapper: StyledComponent<Object, any, any> = styled.div`
  font-size: 11px;
  font-family: 'PT Sans Caption', sans-serif;
  color: #000;
  white-space: nowrap;
`

type Props = {|
  id: number,
  provider: string,
  state: string,
  description?: string,
  rejectedStateConstant: string,
  noBudgetStateConstant: string,
  suspendedStateConstant: string,
  unconnectedStateConstant: string,
  errorStateConstant: string,
  noBudgetHelpPath: string,
  editPath: string,
  connectPath: string,
  activateUrl: string,
  deactivateUrl: string,
  onChange: Function,
  hidePlayPauseButtons: boolean
|}

type State = {|
  isDropdownOpen: boolean
|}

type SwitcherProps = {|
  loading?: boolean,
  checked: boolean,
  onChange?: (boolean) => void
|}

const t = translateFunc(translations.provider_statuses)

export default class ProviderStatus extends Component<Props, State> {
  constructor(props: Props) {
    super(props)

    this.state = { isDropdownOpen: false }
  }

  render() {
    return (
      <ProviderStatusWrapper>
        {!this.props.hidePlayPauseButtons && (
          <AjaxStateSwitcher
            switcherComponent={this.playPauseButton()}
            checked={this.props.state !== this.props.suspendedStateConstant}
            activateUrl={this.props.activateUrl}
            deactivateUrl={this.props.deactivateUrl}
            onResponse={(response) => this.responseReceived(response)}
          />
        )}{' '}
        <div className="inline-block">{this.renderLine()}</div>
      </ProviderStatusWrapper>
    )
  }

  playPauseButton() {
    const { id, provider } = this.props
    return (props: SwitcherProps) => {
      const { checked, loading, onChange } = props
      return (
        <PlayPauseButton
          className={`t-PlayPauseButton_${provider}_${id}`}
          isOn={checked}
          isLoading={loading}
          onChange={onChange}
        />
      )
    }
  }

  responseReceived(response: Object) {
    const { onChange } = this.props
    onChange && onChange(response)
  }

  renderLine() {
    const {
      suspendedStateConstant,
      rejectedStateConstant,
      noBudgetStateConstant,
      unconnectedStateConstant,
      errorStateConstant,
      provider,
      state
    } = this.props

    if (
      _.includes(
        [
          suspendedStateConstant,
          rejectedStateConstant,
          noBudgetStateConstant,
          unconnectedStateConstant,
          errorStateConstant
        ],
        state
      )
    ) {
      let triggerText =
        translations.provider_short_translations[provider] + ' ' + translations.statuses[state]

      return (
        <ExpandingDropdown
          isOpen={this.state.isDropdownOpen}
          triggerText={triggerText}
          className="t-ProviderStatusNote"
          onChange={(isDropdownOpen) => {
            this.setState({ isDropdownOpen })
          }}
          alert
          inlineBlock
        >
          {this.dropdownContent()}
        </ExpandingDropdown>
      )
    }

    return (
      <ProviderStatusNote isActive={state === 'active'}>
        {translations.provider_short_translations[provider]} {translations.statuses[state]}
      </ProviderStatusNote>
    )
  }

  dropdownContent() {
    const {
      state,
      suspendedStateConstant,
      rejectedStateConstant,
      noBudgetStateConstant,
      unconnectedStateConstant,
      errorStateConstant,
      connectPath,
      description,
      editPath,
      noBudgetHelpPath
    } = this.props

    if (state === suspendedStateConstant) {
      return t('suspended_note')
    }
    if (state === unconnectedStateConstant) {
      return (
        <Wrapper>
          <div>{t('unconnected_note')}</div>
          <Spacer />
          <Button href={connectPath}>{t('button_connect_account')}</Button>
        </Wrapper>
      )
    }
    if (state === rejectedStateConstant) {
      return description ? (
        <Wrapper>
          <div dangerouslySetInnerHTML={{ __html: description }} />
          <Spacer />
          <Button href={editPath}>{t('button_edit_advertisement')}</Button>
        </Wrapper>
      ) : (
        t('no_info')
      )
    }
    if (state === noBudgetStateConstant) {
      return (
        <Wrapper>
          {t('no_budget_reason') + ' '}
          <a href={noBudgetHelpPath} target="_blank">
            {t('about_link')}
          </a>
        </Wrapper>
      )
    }
    if (state === errorStateConstant) {
      return <Wrapper className="t-DropdownContent">{t(description)}</Wrapper>
    }
  }
}
